import { NotificationEnum } from "@graphql/generated/graphql.g";
import {
  ComponentProps,
  forwardRef,
  useImperativeHandle,
} from "react";
import { SubmitHandler, useForm } from "react-hook-form";

import { Button } from "@design-system/Button/Button";
import { FieldGroup } from "@design-system/FieldGroup/FieldGroup";
import { Form } from "@design-system/Form/Form";
import { Select } from "@design-system/Select/Select";
import { TextField } from "@design-system/TextField/TextField";
import { InfiniteClientSelect } from "@components/Selects/InfiniteClientSelect";

export type NotificationFilterFormValues = {
  clientId?: string | undefined;
  notificationType?: NotificationEnum | undefined;
  applicationNameSearch?: string | undefined;
};

export type NotificationFilterFormRef = {
  getCurrentValues: () => NotificationFilterFormValues;
};

type Props = {
  submitText?: string;
  onFilterChange: SubmitHandler<NotificationFilterFormValues>;
  values: NotificationFilterFormValues;
  errorsAPI?: ComponentProps<typeof Form>["errorsAPI"];
  disabled?: boolean;
  withSubmitButton?: boolean;
};

export const NotificationFilterForm = forwardRef<
  NotificationFilterFormRef,
  Props
>(({ onFilterChange, values, errorsAPI }, ref) => {
  const { handleSubmit, setValue, watch, getValues } =
    useForm<NotificationFilterFormValues>({
      values: {
        clientId: values.clientId ?? undefined,
        notificationType: values.notificationType ?? undefined,
        applicationNameSearch: values.applicationNameSearch ?? undefined,
      },
    });

  const NOTIFICATION_TYPE_OPTIONS: Array<{
    id: NotificationEnum | "";
    label: string;
  }> = [
    {
      id: "",
      label: "Not selected",
    },
    {
      id: NotificationEnum.SaasDiscovered,
      label: "SaaS Discovered",
    },
    {
      id: NotificationEnum.SharedLogin,
      label: "Shared Login",
    },
  ];

  const clientId = watch("clientId");
  const notificationType = watch("notificationType");
  const applicationNameSearch = watch("applicationNameSearch");

  // Expose getCurrentValues method to parent via ref
  useImperativeHandle(
    ref,
    () => ({
      getCurrentValues: () => getValues(),
    }),
    [getValues],
  );

  const createHandleChange = (
    fieldName: keyof NotificationFilterFormValues,
  ) => {
    return (value: string | string[]) => {
      const enumValue = Array.isArray(value) ? value[0] : value;
      const finalValue = enumValue === "" ? undefined : enumValue;
      setValue(fieldName, finalValue as never, { shouldDirty: true });
    };
  };

  return (
    <Form onSubmit={handleSubmit(onFilterChange)} errorsAPI={errorsAPI}>
      <FieldGroup label="Client">
        <InfiniteClientSelect
          value={clientId}
          onChange={(value) => setValue("clientId", value, { shouldDirty: true })}
          placeholder="Search and select client..."
        />
      </FieldGroup>

      <FieldGroup label="Notification type">
        <Select
          options={NOTIFICATION_TYPE_OPTIONS}
          value={notificationType ?? ""}
          onChange={createHandleChange("notificationType")}
        />
      </FieldGroup>

      <FieldGroup label="Application name">
        <TextField
          placeholder="Search by application name..."
          value={applicationNameSearch ?? ""}
          onChange={(e) =>
            setValue("applicationNameSearch", e.target.value || undefined, {
              shouldDirty: true,
            })
          }
        />
      </FieldGroup>

      {/* Hidden submit button to enable Enter key submission */}
      <Button type="submit" style={{ display: "none" }} />
    </Form>
  );
});
